import * as React from "react";
import type { UseQueryResult } from "@tanstack/react-query";

import { getApiErrorMessage } from "api/apiError";
import type { Integration, IntegrationStatus } from "api/sapSettings";
import { Badge, ErrorState, Skeleton } from "Components/ui";
import { formatDateTime, formatRelative } from "utils/format";

const STATUS: Record<IntegrationStatus, { label: string; tone: "success" | "warning" | "danger" }> =
  {
    ok: { label: "Working", tone: "success" },
    attention: { label: "Check", tone: "warning" },
    missing: { label: "Not set up", tone: "danger" },
  };

/**
 * What's configured on the server (env) and whether it works. Read-only: these are secrets or
 * server wiring, changed on the server and applied with a restart. Never shows a secret.
 */
export const IntegrationsSection: React.FC<{ query: UseQueryResult<Integration[]> }> = ({
  query,
}) => {
  let body: React.ReactNode;
  if (query.isLoading) {
    body = (
      <div className="space-y-3">
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-10" />
        ))}
      </div>
    );
  } else if (query.isError) {
    body = (
      <ErrorState
        compact
        title="Integrations didn't load"
        description={getApiErrorMessage(query.error)}
      />
    );
  } else {
    body = (
      <ul className="divide-y divide-border">
        {(query.data || []).map((item) => {
          const status = STATUS[item.status];
          return (
            <li
              key={item.key}
              className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
            >
              <div className="min-w-0 space-y-0.5">
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.detail}</p>
                {item.at && (
                  <p className="text-xs text-muted-foreground" title={formatDateTime(item.at)}>
                    Last activity {formatRelative(item.at)}
                  </p>
                )}
              </div>
              <Badge tone={status.tone} dot className="shrink-0">
                {status.label}
              </Badge>
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <section aria-label="Integrations" className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-foreground">Integrations</h3>
        <p className="text-sm text-muted-foreground">
          Set on the server and applied with a restart, so they aren&apos;t editable here. Secret
          values are never shown.
        </p>
      </div>
      {body}
    </section>
  );
};

export default IntegrationsSection;
